import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, ChevronLeft, ChevronRight, X, Lock } from "lucide-react";
import { fetchPrograms, fetchNiches } from "@/lib/api";
import { ProgramCard } from "@/components/ProgramCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";

const PAYMENT_METHODS = ["PayPal", "Direct Deposit", "ACH", "Wire", "Payoneer", "Crypto", "Check"];

export default function DirectoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [viewerTier, setViewerTier] = useState("free");

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [niche, setNiche] = useState(searchParams.get("niche") || "all");
  const [commissionType, setCommissionType] = useState("all");
  const [paymentMethod, setPaymentMethod] = useState("all");
  const [beginnerOnly, setBeginnerOnly] = useState(false);
  const [minCookie, setMinCookie] = useState(0);
  const [maxThreshold, setMaxThreshold] = useState(300);
  const [sort, setSort] = useState("popularity");
  const [page, setPage] = useState(1);

  const filters = useMemo(() => ({
    search: search || undefined,
    niche: niche !== "all" ? niche : undefined,
    commission_type: commissionType !== "all" ? commissionType : undefined,
    payment_method: paymentMethod !== "all" ? paymentMethod : undefined,
    beginner_friendly: beginnerOnly || undefined,
    min_cookie: minCookie > 0 ? minCookie : undefined,
    max_threshold: maxThreshold < 300 ? maxThreshold : undefined,
    sort, page, limit: 12,
  }), [search, niche, commissionType, paymentMethod, beginnerOnly, minCookie, maxThreshold, sort, page]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      fetchPrograms(filters)
        .then((d) => { setPrograms(d.programs); setTotal(d.total); setPages(d.pages); setViewerTier(d.viewer_tier || "free"); })
        .catch(console.error)
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [filters]);

  useEffect(() => { fetchNiches().then((d) => setNiches(d.niches)).catch(console.error); }, []);
  useEffect(() => { setPage(1); }, [search, niche, commissionType, paymentMethod, beginnerOnly, minCookie, maxThreshold]);

  const resetFilters = () => {
    setSearch(""); setNiche("all"); setCommissionType("all"); setPaymentMethod("all");
    setBeginnerOnly(false); setMinCookie(0); setMaxThreshold(300); setSearchParams({});
  };

  const FilterPanel = (
    <div className="space-y-8">
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-3">Niche</p>
        <Select value={niche} onValueChange={setNiche}>
          <SelectTrigger data-testid="filter-niche-select" className="rounded-full bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All niches</SelectItem>
            {niches.map((n) => <SelectItem key={n.slug} value={n.slug}>{n.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-3">Commission type</p>
        <Select value={commissionType} onValueChange={setCommissionType}>
          <SelectTrigger data-testid="filter-commission-type-select" className="rounded-full bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any type</SelectItem>
            <SelectItem value="percentage">Percentage</SelectItem>
            <SelectItem value="flat">Flat rate</SelectItem>
            <SelectItem value="recurring">Recurring</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-3">Payment method</p>
        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
          <SelectTrigger data-testid="filter-payment-method-select" className="rounded-full bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any method</SelectItem>
            {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-3">Min cookie duration: <span className="text-slate-900">{minCookie}d</span></p>
        <Slider data-testid="filter-cookie-slider" value={[minCookie]} onValueChange={([v]) => setMinCookie(v)} max={180} step={10} />
      </div>
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-3">Max payout threshold: <span className="text-slate-900">{maxThreshold >= 300 ? "Any" : `$${maxThreshold}`}</span></p>
        <Slider data-testid="filter-threshold-slider" value={[maxThreshold]} onValueChange={([v]) => setMaxThreshold(v)} max={300} step={25} />
      </div>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Beginner friendly only</p>
        <Switch data-testid="filter-beginner-switch" checked={beginnerOnly} onCheckedChange={setBeginnerOnly} />
      </div>
      <button onClick={resetFilters} data-testid="filter-reset-button" className="w-full flex items-center justify-center gap-2 text-sm font-medium py-2.5 rounded-full border border-slate-300 text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors duration-200">
        <X className="w-4 h-4" /> Reset filters
      </button>
    </div>
  );

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <p className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 mb-2">Program Directory</p>
      <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-6">
        {viewerTier === "anon" ? "See what's possible — for free." : "Every program, one directory."}
      </h1>

      {viewerTier === "anon" && (
        <div data-testid="anon-signup-banner" className="mb-10 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-lg">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold opacity-90 mb-2">
              <Lock className="w-4 h-4" /> Members Only
            </div>
            <h3 className="text-2xl font-heading font-bold mb-1">Sign up in 30 seconds to unlock every affiliate program</h3>
            <p className="text-sm text-emerald-50 leading-relaxed max-w-xl">
              You&apos;re seeing 6 preview cards. Real accounts see the full 56-program directory with real signup links — join in one click and start earning today.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <Link
              to="/signup"
              data-testid="anon-cta-signup"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-white text-emerald-700 font-bold hover:bg-emerald-50 transition-colors"
            >
              Sign up free →
            </Link>
            <Link
              to="/login"
              data-testid="anon-cta-login"
              className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-white/40 text-white hover:bg-white/10 transition-colors"
            >
              Log in
            </Link>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 mb-10">
        <div className="relative flex-1">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, company or keyword…"
            data-testid="directory-search-input"
            className="w-full pl-14 pr-4 py-3.5 rounded-full bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="directory-sort-select" className="md:w-56 rounded-full bg-white py-3.5 h-auto"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="popularity">Sort: Popularity</SelectItem>
            <SelectItem value="commission">Sort: Commission</SelectItem>
            <SelectItem value="epc">Sort: EPC</SelectItem>
            <SelectItem value="cookie">Sort: Cookie Duration</SelectItem>
            <SelectItem value="aov">Sort: AOV</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setShowFilters(!showFilters)}
          data-testid="mobile-filter-toggle"
          className="lg:hidden flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-slate-900 text-white text-sm font-medium"
        >
          <SlidersHorizontal className="w-4 h-4" /> Filters
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
        <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
          <div className="lg:sticky lg:top-24 bg-white lg:bg-transparent border lg:border-0 border-slate-200 rounded-lg p-6 lg:p-0">{FilterPanel}</div>
        </aside>
        <div className="lg:col-span-3">
          <p data-testid="directory-result-count" className="text-sm text-slate-500 mb-6">{loading ? "Loading…" : `${total} programs found`}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {programs.map((p) => <ProgramCard key={p.slug} program={p} />)}
          </div>
          {!loading && programs.length === 0 && (
            <div data-testid="directory-empty-state" className="text-center py-20 text-slate-500">No programs match your filters. Try widening your criteria.</div>
          )}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-12">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pagination-prev" className="p-2.5 rounded-full border border-slate-300 disabled:opacity-40 hover:border-slate-900 transition-colors duration-200"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-medium">Page {page} of {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="pagination-next" className="p-2.5 rounded-full border border-slate-300 disabled:opacity-40 hover:border-slate-900 transition-colors duration-200"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
